import React, { useEffect, useMemo, useState } from "react";
import { T } from "../app/tokens.js";
import { ChevronRight, Info } from "lucide-react";
import { Tooltip } from "./Tooltip.jsx";
import { useHoverIntent } from "./useHoverIntent.js";
import { setClientRolePreview } from "../utils/demoRolePreview.js";
import { getDevActAsStatus, changeDevActAs, reloadDevIdentity } from "../data/devActAsClient.js";
import { getDevActAsPeople } from "../data/adminUsersClient.js";
import { searchDirectoryPeople } from "../utils/peopleDirectorySearch.js";

const button = { border: `1px solid ${T.border}`, borderRadius: 6, background: T.card,
  color: T.dark, fontSize: T.fsXs, padding: "5px 8px", cursor: "pointer" };
const row = { display: "grid", gridTemplateColumns: "minmax(40px, 60px) minmax(0, 1fr)",
  alignItems: "center", gap: 10 };
const explanation = "Role preview changes permissions only; to author weekly content, act as a person.";

// DEMO ONLY - backend-confirmed DEV controller, never the effective Person's role.
// This section belongs inside the shared account popover, not a separate modal.
export function DevActAsControl({ currentRole = "admin", onRoleChange }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const info = useHoverIntent();
  const infoId = React.useId();
  const transitionRef = React.useRef(false);
  const searchRef = React.useRef(null);
  useEffect(() => {
    let live = true;
    getDevActAsStatus().then(s => { if (live) setState(s); })
      .catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, []);
  const options = useMemo(() => searchDirectoryPeople(people, query).filter(p =>
    p.active && !p.isDemo && ["admin", "editor", "viewer"].includes(p.provisionedRole)), [people, query]);
  const show = async () => {
    setOpen(true); setError(null);
    searchRef.current?.focus();
    try { setPeople((await getDevActAsPeople()).people); }
    catch (e) { setError(e.message); }
  };
  const change = async (id, preview = null) => {
    if (transitionRef.current) return;
    transitionRef.current = true;
    setBusy(true); setError(null);
    try {
      await changeDevActAs(id);
      // Stop is committed and audited before storing a preview for the reload.
      if (preview) setClientRolePreview(preview);
      reloadDevIdentity();
    }
    catch (e) { setError(e.message); setBusy(false); transitionRef.current = false; }
  };
  const preview = role => {
    if (busy || transitionRef.current || state.invalid) return;
    if (state.person) return change(null, role);
    onRoleChange?.(role);
  };
  // Capability comes only from the backend, never Vite's development flag.
  if (!state?.enabled) return null;
  return <div data-testid="dev-act-as-control" role="group" aria-label="ACT AS" style={{
    borderTop: `1px solid ${T.border}`, padding: "10px 14px", fontSize: T.fsXs,
    width: 380, maxWidth: "var(--dev-act-as-max-width, calc(100vw - 18px))", boxSizing: "border-box" }}>
    <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
      <span style={{ color: T.light, fontWeight: T.fwBold, letterSpacing: "0.06em" }}>ACT AS</span>
      <button type="button" aria-label="About ACT AS" aria-describedby={info.open ? infoId : undefined}
        data-testid="dev-act-as-info" {...info.triggerProps}
        style={{ display: "inline-flex", padding: 0, border: 0, background: "transparent", color: T.mid, cursor: "help" }}>
        <Info size={13} aria-hidden="true" />
      </button>
      {info.open && <Tooltip id={infoId} style={{ top: "calc(100% + 8px)", left: 0,
        width: 260, maxWidth: "100%", boxSizing: "border-box", whiteSpace: "normal", zIndex: 120 }}>
        {explanation}
      </Tooltip>}
    </div>
    {state.invalid && <span role="alert" style={{ color: T.red }}>{state.invalid}</span>}
    <div style={{ ...row, marginBottom: 9 }}>
      <span style={{ color: T.mid }}>Role</span>
      <div role="group" aria-label="Role preview" style={{ display: "flex", padding: 2,
        border: `1px solid ${T.border}`, borderRadius: 8, background: T.hoverBg }}>
      {["admin", "editor", "viewer"].map(role => {
        const selected = !state.person && !state.invalid && currentRole === role;
        const label = role[0].toUpperCase() + role.slice(1);
        return <button key={role} type="button" role="menuitemradio" aria-checked={selected}
          data-testid={`v2-role-option-${role}`} disabled={busy || Boolean(state.invalid)}
          onClick={() => preview(role)} style={{ ...button, flex: 1, minWidth: 0, border: 0,
            padding: "5px 0", fontWeight: T.fwBold, background: selected ? T.card : "transparent",
            color: selected ? T.blue : T.mid, boxShadow: selected ? T.shadowSegment : "none" }}>
          {label}
        </button>;
      })}
      </div>
    </div>
    <div style={row}>
      <span style={{ color: T.mid }}>Person</span>
      {state.person ? <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, minWidth: 0 }}>
        <span role="status" data-testid="dev-act-as-indicator" style={{ color: T.mid, overflowWrap: "anywhere" }}>
          Acting as: {state.person.displayName || state.person.email}
        </span>
        <span aria-hidden="true" style={{ color: T.mid }}>·</span>
        <button type="button" data-testid="dev-act-as-stop" style={{ ...button, border: 0, padding: 0, color: T.blue }}
          onClick={() => change(null)} disabled={busy}>Stop</button>
      </div> : <div style={{ minWidth: 0 }}>
        <button type="button" data-testid="dev-act-as-open" aria-expanded={open}
          style={{ ...button, width: "100%", borderRadius: 8, padding: "6px 10px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, textAlign: "left", color: T.mid }}
          onClick={show} disabled={busy || Boolean(state.invalid)}>
          Act as a person…<ChevronRight size={12} aria-hidden="true" style={{ flexShrink: 0 }} />
        </button>
        {state.invalid && <button type="button" data-testid="dev-act-as-stop" style={{ ...button, marginTop: 4 }}
          onClick={() => change(null)} disabled={busy}>Stop acting as</button>}
      </div>}
    </div>
    {error && <span role="alert" style={{ color: T.red }}>{error}</span>}
    {open && <div style={{ marginTop: 8 }}>
        <input ref={searchRef} autoFocus disabled={busy || Boolean(state.invalid)} aria-label="Search Directory People" data-testid="dev-act-as-search" value={query} onChange={e => setQuery(e.target.value)}
          style={{ ...button, width: "100%", boxSizing: "border-box" }} placeholder="Search name, email or organization" />
        {!people && !error && <p role="status">Loading Directory People...</p>}
        {people && !options.length && <p>No eligible real, active Directory People match this search.</p>}
        <ul style={{ listStyle: "none", padding: 0, maxHeight: 300, overflowY: "auto" }}>
          {options.map(p => <li key={p.id} style={{ marginTop: 6 }}>
            <button type="button" data-testid={`dev-act-as-person-${p.id}`} disabled={busy || Boolean(state.invalid)} style={{ ...button, width: "100%", textAlign: "left", overflowWrap: "anywhere" }}
              onClick={() => change(p.id)}>{p.displayName || p.email} - {p.provisionedRole}<br /><span style={{ color: T.mid }}>{p.email}</span></button>
          </li>)}
        </ul>
    </div>}
  </div>;
}
